import { PageHeader, useExport } from "@dl/web-core-js/shell";
import {
  columns as defineColumns,
  DataTable,
  useServerTable,
  type DataTableHandle,
} from "@dl/web-core-js/table";
import {
  Badge,
  Button,
  Dialog,
  Field,
  FileDrop,
  MenuButton,
  Select,
  toast,
  useConfirm,
  } from "@dl/web-core-js/ui";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Eye, FileText, Images, Store } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ARTWORK_STATUSES,
  RATIOS,
  useApi,
  type FeedType,
  type LazyArtworkDTO,
  type StoreDTO,
} from "../../api";
import { ARTWORK_STATUS_VARIANT, ARTWORK_SYNC_VARIANT } from "../../components/badges";
import { EnumBadge, objectUrl, Thumbnail, TwoLine } from "../../components/cells";
import { AccessDenied, ErrorState, useAllowed } from "../../components/GuardedPage";
import { LogicView } from "../../views";
import {
  AddArtworkImagesDialog,
  DisplayCollectionPickerDialog,
  FeedExportDialog,
  ImportFileDialog,
  ShopifyActionDialog,
  UpdateArtworkCodeDialog,
  type ShopifyChoice,
} from "./dialogs";

type ShopifyAction =
  | "create"
  | "publish"
  | "unpublish"
  | "update-variants"
  | "update-price"
  | "remove"
  | "sync";

/** Artworks per terminate request: each one costs a Shopify call per store it is listed on. */
const TERMINATE_BATCH = 10;

/**
 * The artwork catalogue.
 *
 * One row per artwork with a per-store listing column, because the question
 * this screen exists to answer is "is this piece live on each storefront, and
 * if not why" — everything else on the toolbar acts on the selection.
 *
 * Server-paged: the catalogue is far too large to send whole, and each row
 * costs two correlated subqueries to build. The grid loads a page at a time and
 * hands sorting, filtering and search to the database, which means every
 * filterable column here has to name a field the artwork repository publishes,
 * and every dropdown has to declare its own options — one page of rows is no
 * longer a sample of what exists.
 */
export function ArtworksPage() {
  const { t } = useTranslation();
  const api = useApi();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { start: startExport } = useExport();
  const allowed = useAllowed(LogicView.ARTWORKS);
  const title = t("navigation.item.artworks", "Artworks");

  const grid = useRef<DataTableHandle | null>(null);
  // Two halves of one selection: the rows for what needs an artwork's
  // contents, the ids for what acts on the whole of it — which may reach past
  // the page currently loaded.
  const [selected, setSelected] = useState<LazyArtworkDTO[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [addImages, setAddImages] = useState(false);
  const [corrected, setCorrected] = useState(false);
  const [shopify, setShopify] = useState<ShopifyAction | null>(null);
  const [feedExport, setFeedExport] = useState(false);
  const [productExport, setProductExport] = useState(false);
  const [displayPicker, setDisplayPicker] = useState(false);
  const [importMetadata, setImportMetadata] = useState(false);
  const [importTemplate, setImportTemplate] = useState(false);
  const [templatePublisher, setTemplatePublisher] = useState("");
  const [importMatch, setImportMatch] = useState<"CODE" | "PUBLISHER_CODE">("CODE");
  const [importPublisher, setImportPublisher] = useState("");
  const [renaming, setRenaming] = useState(false);

  const stores = useQuery({
    queryKey: ["store"],
    queryFn: () => api.stores.findAll(),
    enabled: allowed,
  });

  const artworks = useServerTable<LazyArtworkDTO>({
    queryKey: ["artwork", "lazy"],
    fetchPage: (request) => api.lazyArtworks.find(request),
    fetchMatchingIds: (request) => api.lazyArtworks.findMatchingIds(request),
    fetchTotal: () => api.lazyArtworks.count(),
    enabled: allowed,
  });

  const catalogs = useQuery({
    queryKey: ["catalog"],
    queryFn: () => api.catalogs.findAll(),
    enabled: allowed,
  });
  const collections = useQuery({
    queryKey: ["collection"],
    queryFn: () => api.collections.findAll(),
    enabled: allowed,
  });
  const publishers = useQuery({
    queryKey: ["publisher"],
    queryFn: () => api.publishers.findAll(),
    enabled: allowed,
  });

  const storeList = stores.data ?? [];
  const refresh = () => artworks.refresh();

  const single = selected.length === 1 ? selected[0] : undefined;
  const ids = () => selectedIds.map(Number);

  const clearSelection = () => grid.current?.clearSelection();

  // Dropdown choices come from the reference lists this screen already loads,
  // not from the rows on screen — with paging, those are one page of many.
  const options = (values: readonly string[]) =>
    values.map((value) => ({ value, label: value }));

  const catalogOptions = useMemo(
    () => options((catalogs.data ?? []).map((entry) => entry.name)),
    [catalogs.data],
  );
  const collectionOptions = useMemo(
    () => options((collections.data ?? []).map((entry) => entry.name)),
    [collections.data],
  );
  const publisherOptions = useMemo(
    () => options((publishers.data ?? []).map((entry) => entry.name)),
    [publishers.data],
  );

  const cols = useMemo(() => {
    const c = defineColumns<LazyArtworkDTO>();
    const base = [
      c.custom(
        "thumbnail",
        () => "",
        (row) => (
          <Thumbnail
            url={objectUrl(row.thumbnail?.image)}
            alt={row.metadata.code}
            size="md"
            fallback="na"
          />
        ),
        { headerKey: "column.thumbnail", filter: "none", enableSorting: false, size: 64 },
      ),
      c.custom(
        "status",
        (row) => row.status,
        (row) => <EnumBadge value={row.status} variants={ARTWORK_STATUS_VARIANT} />,
        {
          headerKey: "column.status",
          filter: "select",
          filterOptions: options(ARTWORK_STATUSES),
          priority: 3,
        },
      ),
      c.text("code", (row) => row.metadata.code, { headerKey: "column.code", priority: 1 }),
      c.custom(
        "artwork",
        (row) => `${row.metadata.title} ${row.metadata.artist ?? ""}`,
        (row) => <TwoLine primary={row.metadata.title} secondary={row.metadata.artist} />,
        { headerKey: "column.artwork", priority: 1 },
      ),
      c.text("ratio", (row) => row.metadata.ratio, {
        headerKey: "column.ratio",
        filter: "select",
        filterOptions: options(RATIOS),
        priority: 4,
      }),
      c.text("catalog", (row) => row.catalog?.name ?? "", {
        headerKey: "column.catalog",
        filter: "select",
        filterOptions: catalogOptions,
        priority: 3,
      }),
      c.custom(
        "collection",
        (row) => (row.collections ?? []).map((entry) => entry.name).join(", "),
        (row) => {
          const list = row.collections ?? [];
          if (list.length === 0) return <span className="text-ink-faint">—</span>;
          return (
            <TwoLine
              primary={list[0]?.name}
              secondary={list.length > 1 ? `${list.length} collections` : undefined}
            />
          );
        },
        {
          headerKey: "column.collection",
          filter: "select",
          filterOptions: collectionOptions,
          enableSorting: false,
          priority: 4,
        },
      ),
      c.custom(
        "publisher",
        (row) => row.publisher?.name ?? "",
        (row) => <TwoLine primary={row.publisher?.code} secondary={row.publisher?.name} />,
        {
          headerKey: "column.publisher",
          filter: "select",
          filterOptions: publisherOptions,
          priority: 2,
        },
      ),
    ];

    // One column per storefront — "NOT LISTED" is a first-class state, not an
    // absence, which is why it filters like any other value.
    const storeColumns = storeList.map((store: StoreDTO) =>
      c.custom(
        `store-${store.id}`,
        (row) => syncStatus(row, store.id),
        (row) => {
          const status = syncStatus(row, store.id);
          return status === "NOT LISTED" ? (
            <Badge variant="neutral">{status}</Badge>
          ) : (
            <EnumBadge
              value={status as "ACTIVE" | "SUSPENDED"}
              variants={ARTWORK_SYNC_VARIANT}
            />
          );
        },
        {
          header: store.name,
          filter: "select",
          filterOptions: ["NOT LISTED", "ACTIVE", "SUSPENDED"].map((value) => ({
            value,
            label: value,
          })),
          enableSorting: false,
          priority: 5,
        },
      ),
    );

    return [...base, ...storeColumns];
  }, [storeList, catalogOptions, collectionOptions, publisherOptions]);

  const runShopify = async (action: ShopifyAction, choice: ShopifyChoice) => {
    const artworkIds = ids();
    try {
      switch (action) {
        case "create":
          await api.shopifyProducts.create(
            choice.storeId,
            choice.catalog,
            artworkIds,
            choice.categoryIds,
          );
          break;
        case "publish":
          await api.shopifyProducts.publish(choice.storeId, artworkIds);
          break;
        case "unpublish":
          await api.shopifyProducts.unpublish(choice.storeId, artworkIds);
          break;
        case "update-variants":
          await api.shopifyProducts.updateVariants(
            choice.storeId,
            choice.catalog,
            artworkIds,
            choice.categoryIds,
          );
          break;
        case "update-price":
          await api.shopifyProducts.updateVariantsPrice(choice.storeId, artworkIds);
          break;
        case "remove":
          await api.shopifyProducts.remove(choice.storeId, artworkIds);
          break;
        case "sync":
          await api.shopifyProducts.sync(choice.storeId);
          break;
      }
      toast.success(t("notification.sync.complete", "Sync completed"));
      await refresh();
    } catch (failure) {
      toast.error(failure instanceof Error ? failure.message : t("error.generic", "Action failed"));
    }
  };

  const exportMetadata = () =>
    void startExport("artwork-metadata", () => api.artworks.documentMetadata(ids()));

  const exportFeed = async (categoryIds: number[], feedType: FeedType) => {
    await startExport(`feed-${feedType}`, () =>
      api.artworks.documentFeed(feedType, ids(), categoryIds),
    );
  };

  const exportProducts = async (categoryIds: number[]) => {
    await startExport("artwork-products", () => api.artworks.documentProducts(ids(), categoryIds));
  };

  const deleteSelected = async () => {
    const ok = await confirm({
      title: t("dialog.confirm.delete-images.title", "Delete images"),
      message: t("dialog.confirm.delete.text", "Are you sure you want to delete the selected items?"),
      variant: "danger",
    });
    if (!ok) return;
    try {
      await api.artworks.removeBatch(ids());
      toast.success(t("notification.delete.success", "Items deleted successfully"));
      clearSelection();
      await refresh();
    } catch (failure) {
      toast.error(
        failure instanceof Error ? failure.message : t("notification.delete.error", "Failed to delete items"),
      );
    }
  };

  const terminateSelected = async () => {
    const selected = ids();
    const ok = await confirm({
      title: t("dialog.confirm.terminate.title", "Terminate images"),
      message: t(
        "dialog.confirm.terminate.text",
        "The selected images will be set to terminated and removed from every Shopify store they are listed on. Continue?",
      ),
      variant: "danger",
    });
    if (!ok) return;
    try {
      const failures: string[] = [];
      for (let start = 0; start < selected.length; start += TERMINATE_BATCH) {
        failures.push(...(await api.artworks.terminate(selected.slice(start, start + TERMINATE_BATCH))));
      }
      if (failures.length === 0) {
        toast.success(t("notification.terminate.success", "{{count}} images terminated", { count: selected.length }));
      } else {
        toast.error(failures.join(" · "));
      }
      clearSelection();
      await refresh();
    } catch (failure) {
      toast.error(
        failure instanceof Error ? failure.message : t("notification.terminate.error", "Failed to terminate images"),
      );
      await refresh();
    }
  };

  const regenerateProducts = async () => {
    const ok = await confirm({
      title: t("dialog.confirm.regenerate-products.title", "Regenerate products"),
      message: t(
        "dialog.confirm.regenerate-products.text",
        "Are you sure you want to regenerate the products?",
      ),
    });
    if (!ok) return;
    try {
      for (const id of ids()) await api.artworks.regenerateProducts(id);
      toast.success(
        t("notification.regenerate-products.success", "Regenerated products successfully"),
      );
    } catch (failure) {
      toast.error(
        failure instanceof Error
          ? failure.message
          : t("notification.regenerate-products.error", "Failed to regenerate products"),
      );
    }
  };

  const updateMaxPrint = async () => {
    const ok = await confirm({
      title: t("dialog.update.max-print-dimensions.title", "Update artwork max print dimensions"),
      message: t(
        "dialog.update.max-print-dimensions.text",
        "Are you sure you want to update the max print dimensions?",
      ),
    });
    if (!ok) return;
    try {
      await api.artworks.updateMaxPrintDimensions(ids());
      toast.success(
        t("notification.update.max-print-dimensions.started", "Artwork print size update started"),
      );
    } catch (failure) {
      toast.error(
        failure instanceof Error
          ? failure.message
          : t("notification.update.max-print-dimensions.error", "Failed to update artwork print size"),
      );
    }
  };

  const syncDimensions = async () => {
    const ok = await confirm({
      title: t("dialog.sync.dimensions.title", "Sync image dimensions"),
      message: t("dialog.sync.dimensions.text", "Are you sure you want to sync the image dimensions"),
    });
    if (!ok) return;
    try {
      await api.artworkImages.syncDimensions();
      toast.success(t("notification.sync.dimensions.started", "Image dimensions sync started"));
    } catch (failure) {
      toast.error(
        failure instanceof Error
          ? failure.message
          : t("notification.sync.dimensions.error", "Failed to sync image dimensions"),
      );
    }
  };

  const resyncShopify = async () => {
    if (!single) return;
    const ok = await confirm({
      title: t("dialog.resync.shopify.title", "Resync Shopify (fix stale code)"),
      message: t("dialog.resync.shopify.text", "Are you sure you want to resync Shopify ?"),
    });
    if (!ok) return;
    try {
      const failures = await api.artworks.resyncShopify(single.id);
      if (failures.length === 0) {
        toast.success(t("notification.resync.shopify.success", "Shopify sync completed successfully"));
      } else {
        toast.error(failures.join(" · "));
      }
    } catch (failure) {
      toast.error(
        failure instanceof Error
          ? failure.message
          : t("notification.resync.shopify.error", "Failed to sync to Shopify"),
      );
    }
  };

  const addToJoliscreen = async () => {
    const ok = await confirm({
      title: t("dialog.joliscreen.add.title", "Add to Joliscreen"),
      message: t(
        "dialog.joliscreen.add.text",
        "Are you sure you want to add these images to Joliscreen ?",
      ),
    });
    if (!ok) return;
    try {
      await api.joliscreen.add(ids());
      toast.success(
        t("notification.joliscreen.add.started", "Started to add image to Joliscreen"),
      );
    } catch (failure) {
      toast.error(
        failure instanceof Error
          ? failure.message
          : t("notification.joliscreen.add.error", "Failed to add Joliscreen image"),
      );
    }
  };

  if (!allowed) return <AccessDenied title={title} />;
  if (artworks.error) return <ErrorState title={title} error={artworks.error} />;

  const hasSelection = selectedIds.length > 0;
  const isSingle = selectedIds.length === 1;

  return (
    <>
      <PageHeader
        title={title}
        actions={
          <div className="flex items-center gap-2">
            {/* Three menus rather than one with three headings: these are three
                different jobs — retouching a picture, listing it for sale, and
                moving a spreadsheet in or out — and an operator doing one of
                them never wants the other two in the list. */}
            <MenuButton
              label={t("tab.images", "Images")}
              icon={<Images className="size-3.5" />}
              items={[
                {
                  label: t("menu.item.add.images", "Add images"),
                  onClick: () => setAddImages(true),
                },
                {
                  label: t("menu.item.upload.corrected.image", "Upload corrected image"),
                  disabled: !isSingle,
                  onClick: () => setCorrected(true),
                },
                {
                  label: t("menu.item.delete.images", "Delete images"),
                  disabled: !hasSelection,
                  onClick: () => void deleteSelected(),
                },
                {
                  label: t("menu.item.terminate", "Terminate"),
                  disabled: !hasSelection,
                  onClick: () => void terminateSelected(),
                },
                {
                  label: t("menu.item.regenerate.products", "Regen products"),
                  disabled: !hasSelection,
                  onClick: () => void regenerateProducts(),
                },
                {
                  label: t("menu.item.update.max-print-dimensions", "Update max print dimensions"),
                  disabled: !hasSelection,
                  onClick: () => void updateMaxPrint(),
                },
                {
                  label: t("menu.item.sync.dimensions", "Sync image dimensions"),
                  onClick: () => void syncDimensions(),
                },
                {
                  label: t("menu.item.update.artwork.code", "Update code"),
                  disabled: !isSingle,
                  onClick: () => setRenaming(true),
                },
                {
                  label: t("menu.item.add.to.joliscreen", "Add to Joliscreen"),
                  disabled: !hasSelection,
                  onClick: () => void addToJoliscreen(),
                },
                {
                  label: t("menu.item.add-to-display-collection", "Add to display collection"),
                  disabled: !hasSelection,
                  onClick: () => setDisplayPicker(true),
                },
              ]}
            />

            <MenuButton
              label="Shopify"
              icon={<Store className="size-3.5" />}
              items={[
                {
                  label: t("menu.item.shopify.product.create", "Add to shopify"),
                  disabled: !hasSelection,
                  onClick: () => setShopify("create"),
                },
                {
                  label: t("menu.item.shopify.product.publish", "Publish"),
                  disabled: !hasSelection,
                  onClick: () => setShopify("publish"),
                },
                {
                  label: t("menu.item.shopify.product.unpublish", "Unpublish"),
                  disabled: !hasSelection,
                  onClick: () => setShopify("unpublish"),
                },
                {
                  label: t("menu.item.shopify.product.update.variants", "Update variants"),
                  disabled: !hasSelection,
                  onClick: () => setShopify("update-variants"),
                },
                {
                  label: t("menu.item.shopify.product.update.price", "Update price"),
                  disabled: !hasSelection,
                  onClick: () => setShopify("update-price"),
                },
                {
                  label: t("menu.item.shopify.product.remove", "Remove"),
                  disabled: !hasSelection,
                  onClick: () => setShopify("remove"),
                },
                {
                  label: t("menu.item.shopify.product.sync", "Sync"),
                  onClick: () => setShopify("sync"),
                },
                {
                  label: t("menu.item.resync.shopify", "Resync shopify"),
                  disabled: !isSingle,
                  onClick: () => void resyncShopify(),
                },
              ]}
            />

            <MenuButton
              label={t("menu.item.root.document", "Document")}
              icon={<FileText className="size-3.5" />}
              items={[
                {
                  label: t("menu.item.export.metadata", "Export metadata"),
                  disabled: !hasSelection,
                  onClick: exportMetadata,
                },
                {
                  label: t("menu.item.export.products", "Export products"),
                  disabled: !hasSelection,
                  onClick: () => setProductExport(true),
                },
                {
                  label: t("menu.item.export.feed", "Export feed"),
                  disabled: !hasSelection,
                  onClick: () => setFeedExport(true),
                },
                {
                  label: t("menu.item.import.metadata", "Import metadata"),
                  onClick: () => setImportMetadata(true),
                },
                {
                  label: t("menu.item.import.metadata.template", "Import metadata (template)"),
                  onClick: () => setImportTemplate(true),
                },
              ]}
            />
          </div>
        }
      />

      <DataTable<LazyArtworkDTO>
        data={artworks.rows}
        columns={cols}
        getRowId={(row) => String(row.id)}
        stateKey="/artworks"
        loading={artworks.loading || stores.isLoading}
        server={artworks.mode}
        handle={grid}
        selectable
        onSelectionChange={setSelected}
        onSelectionIdsChange={setSelectedIds}
        emptyMessage={t("table.empty", "Nothing to show")}
        rowHeight={52}
        extraBadges={
          <Badge variant="neutral" size="sm">
            {/* Counted by the database over everything the filters match, not
                over the page on screen. */}
            {t("badge.option", "Active: {{count}}", {
              count: artworks.aggregates.active ?? 0,
            })}
          </Badge>
        }
        onRowClick={(row) => void navigate({ to: "/artworks/$id", params: { id: String(row.id) } })}
        rowActions={(row) => (
          <Button
            size="sm"
            variant="ghost"
            className="size-7 p-0"
            title={t("button.edit", "Edit")}
            icon={<Eye className="size-3.5" />}
            onClick={() => void navigate({ to: "/artworks/$id", params: { id: String(row.id) } })}
          />
        )}
      />

      <AddArtworkImagesDialog
        open={addImages}
        onClose={() => setAddImages(false)}
        onUploaded={() => void refresh()}
      />

      <UploadCorrectedImageDialog
        open={corrected}
        onClose={() => setCorrected(false)}
        artworkId={single?.id ?? 0}
        onUploaded={() => void refresh()}
      />

      <ShopifyActionDialog
        open={shopify !== null}
        onClose={() => setShopify(null)}
        title={shopifyTitle(shopify, t)}
        confirmLabel={t("dialog.confirm", "Confirm")}
        withCatalog={shopify === "create" || shopify === "update-variants"}
        onConfirm={(choice) => runShopify(shopify!, choice)}
      />

      <FeedExportDialog
        open={feedExport}
        onClose={() => setFeedExport(false)}
        onConfirm={exportFeed}
      />

      <FeedExportDialog
        products
        open={productExport}
        onClose={() => setProductExport(false)}
        onConfirm={exportProducts}
      />

      <DisplayCollectionPickerDialog
        open={displayPicker}
        onClose={() => setDisplayPicker(false)}
        onConfirm={async (collectionId, name) => {
          try {
            await api.displayCollections.addArtworkImages(collectionId, ids(), "LARGE");
            toast.success(
              `${t("notification.add-image-display-collection.success", "Image added to display collection successfully")} "${name}"`,
            );
          } catch (failure) {
            toast.error(
              failure instanceof Error
                ? failure.message
                : t(
                    "notification.add-image-display-collection.error",
                    "Failed to add image to display collection",
                  ),
            );
          }
        }}
      />

      <ImportFileDialog
        open={importMetadata}
        onClose={() => setImportMetadata(false)}
        title={t("dialog.import.metadata.title", "Import metadata")}
        accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        disabled={importMatch === "PUBLISHER_CODE" && !importPublisher}
        extra={
          <>
            <Field label={t("form.field.import-match", "The file's codes are")} required>
              <Select
                value={importMatch}
                options={[
                  { value: "CODE", label: t("value.import-match.code", "Our codes") },
                  { value: "PUBLISHER_CODE", label: t("value.import-match.publisher-code", "A publisher's codes") },
                ]}
                onChange={(event) => setImportMatch(event.target.value as "CODE" | "PUBLISHER_CODE")}
              />
            </Field>
            {importMatch === "PUBLISHER_CODE" && (
              <Field
                label={t("form.field.publisher", "Publisher")}
                hint={t("hint.import-match.publisher-code", "Read from the publisher_code column when the file has one, else from code.")}
                required
              >
                <Select
                  value={importPublisher}
                  options={(publishers.data ?? []).map((entry) => ({ value: String(entry.id), label: entry.name }))}
                  placeholder={t("form.field.select", "Select")}
                  onChange={(event) => setImportPublisher(event.target.value)}
                />
              </Field>
            )}
          </>
        }
        onImport={(file) =>
          api.artworks.importMetadata(file, importMatch, importMatch === "PUBLISHER_CODE" ? Number(importPublisher) : undefined)
        }
        onImported={() => void refresh()}
      />

      <ImportFileDialog
        open={importTemplate}
        onClose={() => setImportTemplate(false)}
        title={t("dialog.import.metadata-template.title", "Import metadata via publisher template")}
        accept=".csv,.xls,.xlsx,text/csv"
        disabled={!templatePublisher}
        extra={
          <Field label={t("form.field.publisher", "Publisher")} required>
            <Select
              value={templatePublisher}
              options={(publishers.data ?? []).map((entry) => ({
                value: String(entry.id),
                label: entry.name,
              }))}
              placeholder={t("form.field.select", "Select")}
              onChange={(event) => setTemplatePublisher(event.target.value)}
            />
          </Field>
        }
        onImport={(file) =>
          api.metadataTemplates.importMetadata(Number(templatePublisher), file.name, file)
        }
        onImported={() => void refresh()}
      />

      {single && (
        <UpdateArtworkCodeDialog
          open={renaming}
          onClose={() => setRenaming(false)}
          artworkId={single.id}
          currentCode={single.metadata.code}
          publisherPrefix={single.publisher?.code ?? ""}
          onDone={() => void refresh()}
        />
      )}

      {/* Keep reference queries mounted so the filter dropdowns stay populated. */}
      <span hidden>{catalogs.data?.length}{collections.data?.length}</span>
    </>
  );
}

function syncStatus(row: LazyArtworkDTO, storeId: number): string {
  const sync = (row.store_syncs ?? []).find((entry) => entry.store?.id === storeId);
  return sync ? sync.status : "NOT LISTED";
}

function shopifyTitle(action: ShopifyAction | null, t: (k: string, d: string) => string): string {
  switch (action) {
    case "create":
      return t("dialog.shopify-product-create.title", "Create Shopify products");
    case "publish":
      return t("dialog.shopify-product-publish.title", "Publish products");
    case "unpublish":
      return t("dialog.shopify-product-unpublish.title", "Unpublish products");
    case "update-variants":
      return t("dialog.shopify-product-update-variants.title", "Update variants");
    case "update-price":
      return t("dialog.shopify-product-update-price.title", "Update prices");
    case "remove":
      return t("dialog.shopify-product-remove.title", "Remove products");
    case "sync":
      return t("dialog.shopify-product-sync.title", "Synchronize Shopify products");
    default:
      return "";
  }
}

/**
 * Replaces an artwork's HR image with a colour-corrected one. It overwrites
 * whatever correction exists, which is why the dialog says so before the pick.
 */
function UploadCorrectedImageDialog({
  open,
  onClose,
  artworkId,
  onUploaded,
}: {
  open: boolean;
  onClose: () => void;
  artworkId: number;
  onUploaded: () => void;
}) {
  const { t } = useTranslation();
  const api = useApi();
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!file || !artworkId) return;
    setBusy(true);
    try {
      await api.artworkImages.uploadCorrectedImage(artworkId, file);
      toast.success(
        t("notification.upload-corrected-image.success", "Corrected image uploaded successfully"),
      );
      setFile(null);
      onUploaded();
      onClose();
    } catch (failure) {
      toast.error(
        failure instanceof Error
          ? failure.message
          : t("notification.upload-corrected-image.error", "Failed to upload corrected image"),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("dialog.upload-corrected-image.title", "Upload correct image")}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("dialog.cancel", "Cancel")}
          </Button>
          <Button variant="primary" loading={busy} disabled={!file} onClick={() => void submit()}>
            {t("dialog.confirm.upload", "Upload")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="rounded-md bg-warn-soft px-3 py-2 text-[13px] text-warn">
          {t(
            "dialog.upload-corrected-image.warning",
            "Any existing corrected image will be overwritten",
          )}
        </p>
        <FileDrop
          accept=".jpg,.jpeg,.tif,.tiff,image/jpeg,image/tiff"
          files={file ? [file] : []}
          onRemove={() => setFile(null)}
          onFiles={(files) => setFile(files[0] ?? null)}
        />
      </div>
    </Dialog>
  );
}
